// src/components/policies/HICoverageEditor.jsx
// Hospital Indemnity coverage configuration editor

import React from 'react';
import {
  Building2, Truck, Activity, Heart, Pill, Stethoscope, Check
} from 'lucide-react';

// Define all available HI coverages
const HI_COVERAGES = [
  {
    key: 'daily_hospital',
    label: 'Daily Hospital Benefit',
    icon: Building2,
    suffix: '/day',
    placeholder: '500',
    description: 'Per day benefit for hospital stays'
  },
  {
    key: 'daily_hospital_days',
    label: 'Days Covered',
    icon: Building2,
    suffix: ' days',
    placeholder: '5',
    isCount: true,
    description: 'Number of days covered'
  },
  {
    key: 'icu_daily',
    label: 'ICU Daily Benefit',
    icon: Activity,
    suffix: '/day',
    placeholder: '1000',
    description: 'Additional benefit for ICU stay'
  },
  {
    key: 'ambulance',
    label: 'Ambulance Benefit',
    icon: Truck,
    suffix: '',
    placeholder: '350',
    description: 'Ground ambulance transport'
  },
  {
    key: 'outpatient_surgery',
    label: 'Outpatient Surgery',
    icon: Stethoscope,
    suffix: '',
    placeholder: '500',
    description: 'Same-day surgery procedures'
  },
  {
    key: 'er_visit',
    label: 'ER Visit',
    icon: Activity,
    suffix: '',
    placeholder: '250',
    description: 'Emergency room visit benefit'
  },
  {
    key: 'cancer_rider',
    label: 'Cancer Diagnosis',
    icon: Heart,
    suffix: '',
    placeholder: '5000',
    description: 'Lump sum cancer diagnosis benefit'
  },
  {
    key: 'heart_attack',
    label: 'Heart Attack',
    icon: Heart,
    suffix: '',
    placeholder: '5000',
    description: 'Lump sum heart attack benefit'
  },
  {
    key: 'stroke',
    label: 'Stroke',
    icon: Activity,
    suffix: '',
    placeholder: '5000',
    description: 'Lump sum stroke benefit'
  },
  {
    key: 'skilled_nursing',
    label: 'Skilled Nursing',
    icon: Pill,
    suffix: '/day',
    placeholder: '200',
    description: 'Daily benefit for skilled nursing facility'
  }
];

export default function HICoverageEditor({ coverages = {}, onChange }) {
  const handleToggle = (key) => {
    const newCoverages = { ...coverages };
    if (newCoverages[key]) {
      delete newCoverages[key];
    } else {
      // Set a default value based on placeholder
      const coverage = HI_COVERAGES.find(c => c.key === key);
      newCoverages[key] = parseInt(coverage?.placeholder || '0');
    }
    onChange(newCoverages);
  };

  const handleValueChange = (key, value) => {
    const newCoverages = { ...coverages };
    const numValue = parseInt(value) || 0;
    if (numValue > 0) {
      newCoverages[key] = numValue;
    } else {
      delete newCoverages[key];
    }
    onChange(newCoverages);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <Truck className="w-4 h-4 text-blue-500" />
          Coverage Details
        </h4>
        <span className="text-xs text-slate-500">
          {Object.keys(coverages).length} selected
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {HI_COVERAGES.map((coverage) => {
          const Icon = coverage.icon;
          const isActive = coverages[coverage.key] > 0;

          return (
            <div
              key={coverage.key}
              className={`relative p-3 rounded-xl border-2 transition-all cursor-pointer ${
                isActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              onClick={() => !isActive && handleToggle(coverage.key)}
            >
              <div className="flex items-start gap-3">
                {/* Toggle Checkbox */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(coverage.key);
                  }}
                  className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all ${
                    isActive
                      ? 'bg-blue-500 text-white'
                      : 'border-2 border-slate-300 hover:border-blue-400'
                  }`}
                >
                  {isActive && <Check className="w-3.5 h-3.5" />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${isActive ? 'text-blue-800' : 'text-slate-700'}`}>
                      {coverage.label}
                    </span>
                  </div>

                  {isActive && (
                    <div className="mt-2 flex items-center gap-2">
                      {!coverage.isCount && (
                        <span className="text-slate-500">$</span>
                      )}
                      <input
                        type="number"
                        value={coverages[coverage.key] || ''}
                        onChange={(e) => handleValueChange(coverage.key, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder={coverage.placeholder}
                        className="w-24 px-2 py-1 text-sm border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white"
                      />
                      <span className="text-sm text-slate-500">{coverage.suffix}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {Object.keys(coverages).length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-700 font-medium mb-2">Selected Coverages:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(coverages).map(([key, value]) => {
              const coverage = HI_COVERAGES.find(c => c.key === key);
              if (!coverage) return null;
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded-lg text-xs font-medium text-blue-700"
                >
                  <Check className="w-3 h-3" />
                  {coverage.label}: {coverage.isCount ? '' : '$'}{value.toLocaleString()}{coverage.suffix}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
